// Family search + a scrollable variant picker table: the buyer narrows a
// family's variants with multi-select filters (Brand/Origin/State/Spec plus
// every UOM dimension) and clicks a row to pick one. Selection state lives in
// the parent (wizard draft); this component fetches the family's variants,
// renders the filter bar + table, and reports every change through onPatch.
// Column/filter order mirrors the family workspace's variants grid (see
// family-workspace/variants-grid/) so the two screens read the same way.
// See utils/variant-elimination for the filter-matching rules.
//
// The Attributes column is DISPLAY ONLY — it has no filter and takes no part in
// the "N of M variants match" count. Two variants of a family can be identical
// on every other visible column and differ only on an attribute, so hiding them
// made operators pick the wrong variant; but `attrValue` indexes MasterProduct
// scalars directly and cannot express an EAV lookup, so filtering on attributes
// would mean reworking the elimination model.

import {
  Box,
  ChakraAsyncSelect,
  ChakraSelect,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Image,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
} from '@elkaso-app/web-design';
import { MasterProductFamilyOption, useSearchMasterProductFamiliesApi } from 'apis/master-product-families';
import { MasterProduct, useGetMasterProductsApi } from 'apis/master-products';
import { AttributeDictionaryType, useGetAttributeDictionaryApi } from 'apis/product-attributes';
import { LabelWithHint, UOM_HELP } from 'pages/inquiry-processor/components/master-product-families/uom-field-meta';
import { attributeChips } from 'pages/inquiry-processor/utils/attribute-chips';
import {
  AttrFilters,
  attrOptions,
  AttrSelections,
  attrValue,
  EliminationAttr,
  ELIMINATION_ATTRS,
  filterVariants,
} from 'pages/inquiry-processor/utils/variant-elimination';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiImage } from 'react-icons/fi';

export interface VariantEliminationPatch {
  familyId?: number;
  familyName?: string;
  sel?: AttrSelections;
  masterProductId?: number;
}

interface VariantEliminationFieldsProps {
  familyId?: number;
  familyName: string;
  masterProductId?: number;
  // Id of a variant just created via the inline form (changes on every
  // creation) — clears the filter bar so a variant the current filters
  // wouldn't match still shows up, already selected.
  justCreatedVariantId?: number;
  // Master products already on the inquiry — hidden from the variant pool so
  // two items can't resolve to the same variant. The family itself stays
  // pickable: an inquiry may carry several variants of one family.
  excludeIds: number[];
  // Variant to keep selectable even if inactive — edit mode passes the item's
  // current product so deactivation doesn't dead-end the wizard.
  keepVariantId?: number;
  onPatch: (patch: VariantEliminationPatch) => void;
  onCreateFamily: (typedName: string) => void;
  onCreateVariant: () => void;
}

// <option value> can't distinguish "no selection" from "the empty attribute
// value" (both would be '') — variants may legitimately have no brand/origin/
// spec, so the empty VALUE gets a sentinel in the multi-select options.
const NONE = '__none__';

const FILTER_ORDER: EliminationAttr[] = [
  'brand',
  'origin',
  'state',
  'baseUnit',
  'itemUnit',
  'netContent',
  'packUnit',
  'packSize',
  'spec',
];
const FILTER_META: Record<EliminationAttr, { label: string; hint?: string }> = {
  brand: { label: 'Brand' },
  origin: { label: 'Origin' },
  state: { label: 'State' },
  spec: { label: 'Spec' },
  baseUnit: UOM_HELP.baseUnit,
  itemUnit: UOM_HELP.itemUnit,
  netContent: UOM_HELP.netContent,
  packUnit: UOM_HELP.packUnit,
  packSize: UOM_HELP.packSize,
};

type FilterOption = { value: string; label: string };
const toOption = (v: string): FilterOption => ({ value: v || NONE, label: v || 'N/A' });

// Every chip is shown, with no "+N" overflow. The family workspace grid
// truncates at 3 because its drawer holds the full truth; here the chips ARE
// the truth the operator is choosing on, so hiding any of them reintroduces
// exactly the blindness this column exists to fix.
const AttributesCell = ({
  attributes,
  dictionary,
}: {
  attributes: MasterProduct['attributes'];
  dictionary: AttributeDictionaryType[] | undefined;
}) => {
  const chips = attributeChips(attributes, dictionary);
  if (!chips.length) return <>—</>;
  return (
    <Wrap spacing='1'>
      {chips.map((chip) => (
        <WrapItem key={`${chip.type}:${chip.value}`}>
          <Tag size='sm'>{chip.label}</Tag>
        </WrapItem>
      ))}
    </Wrap>
  );
};

export const VariantEliminationFields = ({
  familyId,
  familyName,
  masterProductId,
  justCreatedVariantId,
  excludeIds,
  keepVariantId,
  onPatch,
  onCreateFamily,
  onCreateVariant,
}: VariantEliminationFieldsProps) => {
  const { loadOptions } = useSearchMasterProductFamiliesApi();
  const [defaultOptions, setDefaultOptions] = useState<MasterProductFamilyOption[]>([]);
  useEffect(() => {
    loadOptions('', setDefaultOptions);
  }, [loadOptions]);

  // Cached per familyId (react-query) — cheap across cards on the same family.
  const { data, isFetching } = useGetMasterProductsApi(familyId);
  // Unscoped: this cell only LABELS values the variants already carry, it never
  // offers a choice, so narrowing the dictionary by category would gain nothing
  // and could only drop labels for a legacy value outside the category.
  const { data: dictionaryData } = useGetAttributeDictionaryApi();
  const dictionary = dictionaryData?.data;

  const excludeKey = excludeIds.join(',');
  const variants = useMemo(
    () => (data?.data ?? []).filter((p) => (p.isActive || p.id === keepVariantId) && !excludeIds.includes(p.id)),
    [data, excludeKey, keepVariantId]
  );

  const [filters, setFilters] = useState<AttrFilters>({});
  // A new family starts with a clean filter bar.
  useEffect(() => {
    setFilters({});
  }, [familyId]);
  // A variant just created inline might not match the current filters —
  // clear them so it's visible (it's already selected via masterProductId).
  useEffect(() => {
    if (justCreatedVariantId) setFilters({});
  }, [justCreatedVariantId]);

  const filtered = useMemo(() => filterVariants(variants, filters), [variants, filters]);
  const hasFilters = Object.keys(filters).length > 0;

  const pickFamily = (opt: MasterProductFamilyOption | null) => {
    if (opt?.value === familyId) return;
    // New family (or cleared) → filters and the current pick reset.
    onPatch({ familyId: opt?.value, familyName: opt?.label ?? '', sel: {}, masterProductId: undefined });
  };

  const selectVariant = (p: MasterProduct) => {
    if (p.id === masterProductId) {
      // Click the selected row again to deselect it.
      onPatch({ sel: {}, masterProductId: undefined });
      return;
    }
    const nextSel: AttrSelections = {};
    ELIMINATION_ATTRS.forEach((k) => {
      nextSel[k] = attrValue(p, k);
    });
    onPatch({ sel: nextSel, masterProductId: p.id });
  };

  const setFilter = (attr: EliminationAttr, values: string[]) => {
    setFilters((f) => {
      const next = { ...f };
      if (values.length) next[attr] = values;
      else delete next[attr];
      return next;
    });
  };

  // Empty-state of the family dropdown: offer inline creation of the typed
  // name. onMouseDown because react-select swallows clicks inside the menu.
  // Stable component identity across renders (react-select treats a new
  // function as a new component type and remounts the subtree); the ref keeps
  // the latest onCreateFamily reachable without re-creating the component.
  const onCreateFamilyRef = useRef(onCreateFamily);
  onCreateFamilyRef.current = onCreateFamily;
  const NoOptionsCreateLink = useMemo(
    () =>
      function NoOptionsCreateLink(props: { selectProps?: { inputValue?: string } }) {
        const typed = props.selectProps?.inputValue?.trim() || '';
        return (
          <Box p='3' textAlign='center'>
            <Text fontSize='sm' color='gray.500' mb='1'>
              No matching master product family
            </Text>
            <Text
              as='button'
              type='button'
              fontSize='sm'
              fontWeight='600'
              color='red.500'
              _hover={{ textDecoration: 'underline' }}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onCreateFamilyRef.current(typed);
              }}>
              + Create {typed ? `"${typed}"` : 'a new family'}
            </Text>
          </Box>
        );
      },
    []
  );

  const filterSelect = (attr: EliminationAttr) => {
    const options = attrOptions(variants, attr).map(toOption);
    const value = (filters[attr] ?? []).map(toOption);
    return (
      <ChakraSelect
        size='sm'
        isMulti
        isClearable
        closeMenuOnSelect={false}
        options={options}
        value={value}
        onChange={(opts: any) =>
          setFilter(
            attr,
            ((opts as FilterOption[]) ?? []).map((o) => (o.value === NONE ? '' : o.value))
          )
        }
        isDisabled={!familyId || !variants.length}
        placeholder='Any'
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
      />
    );
  };

  return (
    <Box>
      <FormControl mb='3' isRequired>
        {/* "+ New family" is always visible — the NoOptionsMessage link below
            only appears when a search comes back empty, which hid inline
            creation whenever anything vaguely similar existed. */}
        <Flex justify='space-between' align='center'>
          <FormLabel fontSize='sm' mb='1'>
            Master product family
          </FormLabel>
          <Text
            as='button'
            type='button'
            fontSize='sm'
            fontWeight='600'
            color='red.500'
            _hover={{ textDecoration: 'underline' }}
            onClick={() => onCreateFamily('')}>
            + New family
          </Text>
        </Flex>
        <ChakraAsyncSelect
          size='sm'
          loadOptions={loadOptions}
          defaultOptions={defaultOptions}
          components={{ NoOptionsMessage: NoOptionsCreateLink }}
          value={familyId ? { value: familyId, label: familyName } : null}
          onChange={(opt: any) => pickFamily(opt || null)}
          isClearable
          placeholder='Search items…'
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
        />
      </FormControl>

      {familyId && isFetching && !data && (
        <Flex justify='center' py='6'>
          <Spinner size='sm' color='gray.400' />
        </Flex>
      )}

      {familyId && data && variants.length === 0 && (
        <Text fontSize='sm' color='orange.500' mb='2'>
          No available variants for this family — create one below.
        </Text>
      )}

      {familyId && data && variants.length > 0 && (
        <Box>
          <Flex justify='space-between' align='center' mb='1'>
            <Text fontSize='xs' color='gray.600' fontWeight='600'>
              Narrow down by:
            </Text>
            {hasFilters && (
              <Text
                as='button'
                type='button'
                fontSize='xs'
                fontWeight='600'
                color='red.500'
                _hover={{ textDecoration: 'underline' }}
                onClick={() => setFilters({})}>
                Clear all filters
              </Text>
            )}
          </Flex>
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }} gap='2' mb='3'>
            {FILTER_ORDER.map((attr) => (
              <FormControl key={attr}>
                <FormLabel fontSize='xs' mb='1' color='gray.600'>
                  {FILTER_META[attr].hint ? (
                    <LabelWithHint label={FILTER_META[attr].label} hint={FILTER_META[attr].hint as string} />
                  ) : (
                    FILTER_META[attr].label
                  )}
                </FormLabel>
                {filterSelect(attr)}
              </FormControl>
            ))}
          </Grid>

          <Text fontSize='xs' color='gray.500' mb='1'>
            {filtered.length} of {variants.length} variants match
          </Text>

          {filtered.length === 0 ? (
            <Text fontSize='sm' color='orange.500' py='4' textAlign='center'>
              No variant matches these filters — create it below, or clear a filter.
            </Text>
          ) : (
            <Box maxH='320px' overflow='auto' borderWidth='1px' rounded='md'>
              <Table size='sm' variant='simple'>
                <Thead position='sticky' top='0' bg='white' zIndex={1} boxShadow='sm'>
                  <Tr>
                    <Th>Photo</Th>
                    <Th>Brand</Th>
                    <Th>Origin</Th>
                    <Th>State</Th>
                    {/* Beside the other identity descriptors, NOT after the five
                        UOM columns — the operator picking the wrong variant is
                        the bug, and a column they never scroll to doesn't fix it. */}
                    <Th>Attributes</Th>
                    <Th>
                      <LabelWithHint label={UOM_HELP.baseUnit.label} hint={UOM_HELP.baseUnit.hint} />
                    </Th>
                    <Th>
                      <LabelWithHint label={UOM_HELP.itemUnit.label} hint={UOM_HELP.itemUnit.hint} />
                    </Th>
                    <Th>
                      <LabelWithHint label={UOM_HELP.netContent.label} hint={UOM_HELP.netContent.hint} />
                    </Th>
                    <Th>
                      <LabelWithHint label={UOM_HELP.packUnit.label} hint={UOM_HELP.packUnit.hint} />
                    </Th>
                    <Th>
                      <LabelWithHint label={UOM_HELP.packSize.label} hint={UOM_HELP.packSize.hint} />
                    </Th>
                    <Th>Spec</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filtered.map((p) => {
                    const isSelected = p.id === masterProductId;
                    return (
                      <Tr
                        key={p.id}
                        onClick={() => selectVariant(p)}
                        // Legacy global CSS (_tables.scss) forces `tr { cursor: default
                        // !important }` and a zebra-stripe background on every table row
                        // in the app — !important is required to win against it here.
                        cursor='pointer !important'
                        bg={isSelected ? 'red.50 !important' : undefined}
                        _hover={{ bg: isSelected ? 'red.50 !important' : 'gray.50 !important' }}>
                        <Td>
                          {p.imageUrl ? (
                            <Image src={p.imageUrl} alt='' boxSize='24px' objectFit='cover' rounded='sm' />
                          ) : (
                            <Box color='gray.300' fontSize='16px' px='2'>
                              <FiImage />
                            </Box>
                          )}
                        </Td>
                        <Td>{p.brand || '—'}</Td>
                        <Td>{p.origin || '—'}</Td>
                        <Td>{p.state || '—'}</Td>
                        <Td>
                          <AttributesCell attributes={p.attributes} dictionary={dictionary} />
                        </Td>
                        <Td>{p.baseUnit || '—'}</Td>
                        <Td>{p.itemUnit || '—'}</Td>
                        <Td>{p.netContent ?? '—'}</Td>
                        <Td>{p.packUnit || '—'}</Td>
                        <Td>{p.packSize ?? '—'}</Td>
                        <Td>{p.spec || '—'}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      {familyId && (
        <Flex justify='flex-end' mt='2'>
          <Text
            as='button'
            type='button'
            fontSize='sm'
            fontWeight='600'
            color='red.500'
            _hover={{ textDecoration: 'underline' }}
            onClick={onCreateVariant}>
            + New variant in this family
          </Text>
        </Flex>
      )}
    </Box>
  );
};
